import { ArrowDownTrayIcon, EyeIcon, FunnelIcon, MagnifyingGlassIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { ADMIN_CONFIG } from "../../config/adminConfig";
import { useAdmin } from "../../contexts/AdminContext";
import { adminService } from "../../services/api";
import OrderDetailsDialog from "./OrderDetailsDialog";

const OrderManagement = () => {
    const { refreshOrders, refreshKey } = useAdmin();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchInput, setSearchInput] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [statusFilter, setStatusFilter] = useState("");
    const [paymentStatusFilter, setPaymentStatusFilter] = useState("");
    const [deliveryTypeFilter, setDeliveryTypeFilter] = useState("");
    const [showOrderDialog, setShowOrderDialog] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [orderStatuses, setOrderStatuses] = useState([]);
    const searchTimeoutRef = useRef(null);

    const [allOrders, setAllOrders] = useState([]);

    const fetchOrders = useCallback(async () => {
        try {
            setLoading(true);
            const filters = {
                page: 1,
                limit: 10000,
            };

            if (statusFilter) {
                filters.status = statusFilter;
            }

            const response = await adminService.getAllOrders(filters);
            const fetchedOrders = response.data.orders || [];
            setAllOrders(fetchedOrders);
        } catch (error) {
            console.error("Failed to fetch orders:", error);
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => {
        let filtered = [...allOrders];

        if (searchTerm && searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase().trim();
            filtered = allOrders.filter(order => {
                const orderId = (order.orderNumber || order._id.slice(-6)).toLowerCase();
                const customerName = (
                    order.user?.name ||
                    `${order.shippingAddress?.firstName || ""} ${order.shippingAddress?.lastName || ""}`.trim() ||
                    "Guest"
                ).toLowerCase();
                const customerEmail = (order.user?.email || order.shippingAddress?.email || "").toLowerCase();
                const customerPhone = (order.shippingAddress?.phone || order.user?.phone || "").toLowerCase();

                return (
                    orderId.includes(searchLower) ||
                    customerName.includes(searchLower) ||
                    customerEmail.includes(searchLower) ||
                    customerPhone.includes(searchLower)
                );
            });
        }

        if (paymentStatusFilter) {
            filtered = filtered.filter(order => {
                const paymentStatus = order.payment?.status || "pending";
                return paymentStatus === paymentStatusFilter;
            });
        }

        if (deliveryTypeFilter) {
            filtered = filtered.filter(order => {
                const deliveryType = order.deliveryType || "standard";
                return deliveryType === deliveryTypeFilter;
            });
        }

        const totalFiltered = filtered.length;
        const totalPages = Math.ceil(totalFiltered / ADMIN_CONFIG.pagination.defaultLimit);
        setTotalPages(totalPages || 1);

        const startIndex = (currentPage - 1) * ADMIN_CONFIG.pagination.defaultLimit;
        const endIndex = startIndex + ADMIN_CONFIG.pagination.defaultLimit;
        const paginatedOrders = filtered.slice(startIndex, endIndex);
        setOrders(paginatedOrders);
    }, [allOrders, searchTerm, currentPage, paymentStatusFilter, deliveryTypeFilter]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders, refreshKey]);

    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            setSearchTerm(searchInput);
            setCurrentPage(1);
        }, 500);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchInput]);

    useEffect(() => {
        setCurrentPage(1);
    }, [statusFilter, paymentStatusFilter, deliveryTypeFilter]);

    useEffect(() => {
        const fetchOrderStatuses = async () => {
            try {
                const response = await adminService.getOrderStatuses();
                if (response.status === "success" && response.data?.statuses) {
                    setOrderStatuses(response.data.statuses);
                } else {
                    setOrderStatuses([
                        "pending", "confirmed", "designing", "proof-sent",
                        "proof-approved", "printing", "quality-check", "packed",
                        "shipped", "delivered", "cancelled", "returned"
                    ]);
                }
            } catch (error) {
                console.error("Failed to fetch order statuses:", error);
                setOrderStatuses([
                    "pending", "confirmed", "designing", "proof-sent",
                    "proof-approved", "printing", "quality-check", "packed",
                    "shipped", "delivered", "cancelled", "returned"
                ]);
            }
        };
        fetchOrderStatuses();
    }, []);

    const handleStatusUpdate = async (orderId, newStatus) => {
        try {
            await adminService.updateOrderStatus(orderId, newStatus);
            await refreshOrders();
            fetchOrders();
        } catch (error) {
            console.error("Failed to update order status:", error);
            alert("Failed to update order status");
        }
    };

    const handlePaymentStatusUpdate = async (orderId, newPaymentStatus, notes = "") => {
        try {
            await adminService.updateOrderPaymentStatus(orderId, newPaymentStatus, notes);
            await refreshOrders();
            fetchOrders();
            alert("Payment status updated successfully");
        } catch (error) {
            console.error("Failed to update payment status:", error);
            alert("Failed to update payment status");
        }
    };

    const handleDeleteOrder = async (orderId, orderNumber) => {
        const confirmMessage = `Are you sure you want to delete order #${orderNumber || orderId.slice(-6)}?\n\nThis action cannot be undone.`;
        if (!window.confirm(confirmMessage)) {
            return;
        }

        try {
            await adminService.deleteOrder(orderId);
            await refreshOrders();
            fetchOrders();
            alert("Order deleted successfully");
        } catch (error) {
            console.error("Failed to delete order:", error);
            alert("Failed to delete order");
        }
    };

    const handleViewOrder = orderId => {
        setSelectedOrderId(orderId);
        setShowOrderDialog(true);
    };

    const handleCloseDialog = () => {
        setShowOrderDialog(false);
        setSelectedOrderId(null);
    };

    const handleExportToExcel = async () => {
        try {
            let ordersToExport = [...allOrders];

            if (searchTerm && searchTerm.trim()) {
                const searchLower = searchTerm.toLowerCase().trim();
                ordersToExport = allOrders.filter(order => {
                    const orderId = (order.orderNumber || order._id.slice(-6)).toLowerCase();
                    const customerName = (
                        order.user?.name ||
                        `${order.shippingAddress?.firstName || ""} ${order.shippingAddress?.lastName || ""}`.trim() ||
                        "Guest"
                    ).toLowerCase();
                    const customerEmail = (order.user?.email || order.shippingAddress?.email || "").toLowerCase();
                    const customerPhone = (order.shippingAddress?.phone || order.user?.phone || "").toLowerCase();

                    return (
                        orderId.includes(searchLower) ||
                        customerName.includes(searchLower) ||
                        customerEmail.includes(searchLower) ||
                        customerPhone.includes(searchLower)
                    );
                });
            }

            if (paymentStatusFilter) {
                ordersToExport = ordersToExport.filter(order => {
                    const paymentStatus = order.payment?.status || "pending";
                    return paymentStatus === paymentStatusFilter;
                });
            }

            if (deliveryTypeFilter) {
                ordersToExport = ordersToExport.filter(order => {
                    const deliveryType = order.deliveryType || "standard";
                    return deliveryType === deliveryTypeFilter;
                });
            }

            if (ordersToExport.length === 0) {
                alert("No orders to export");
                return;
            }

            const exportData = ordersToExport.map(order => ({
                "Order ID": order.orderNumber || order._id.slice(-6),
                "Order Date": new Date(order.createdAt).toLocaleDateString(),
                "Customer Name": order.user?.name ||
                    `${order.shippingAddress?.firstName || ""} ${order.shippingAddress?.lastName || ""}`.trim() ||
                    "Guest",
                "Customer Email": order.user?.email || order.shippingAddress?.email || "N/A",
                "Customer Phone": order.shippingAddress?.phone || order.user?.phone || "N/A",
                "Total Amount": order.pricing?.total || order.totalAmount || 0,
                "Items Count": order.items?.length || 0,
                "Payment Status": order.payment?.status || "pending",
                "Delivery Type": order.deliveryType === 'express' ? 'Express' :
                    order.deliveryType === 'standard' ? 'Standard' :
                        order.deliveryType === 'self-pickup' ? 'Self Pickup' :
                            order.deliveryType || 'Standard',
                "Order Status": order.status || "pending",
                "Shipping Address": order.shippingAddress ?
                    (order.deliveryType === 'self-pickup' || order.shippingAddress.addressLine1 === 'Pick from store'
                        ? "Pick from store"
                        : `${order.shippingAddress.address || order.shippingAddress.addressLine1 || ""}, ${order.shippingAddress.city || ""}, ${order.shippingAddress.state || ""} - ${order.shippingAddress.pincode || ""}`.trim()) :
                    "N/A",
            }));

            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Orders");

            const fileName = `orders_export_${new Date().toISOString().split("T")[0]}.xlsx`;
            XLSX.writeFile(workbook, fileName);
        } catch (error) {
            console.error("Failed to export orders:", error);
            alert("Failed to export orders. Please try again.");
        }
    };

    const getStatusColor = status => {
        const colors = {
            pending: "bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-800 border-yellow-200",
            confirmed: "bg-gradient-to-r from-blue-100 to-sky-100 text-blue-800 border-blue-200",
            designing: "bg-gradient-to-r from-purple-100 to-violet-100 text-purple-800 border-purple-200",
            "proof-sent": "bg-gradient-to-r from-indigo-100 to-blue-100 text-indigo-800 border-indigo-200",
            "proof-approved": "bg-gradient-to-r from-green-100 to-emerald-100 text-[#008000] border-green-200",
            printing: "bg-gradient-to-r from-orange-100 to-amber-100 text-orange-800 border-orange-200",
            "quality-check": "bg-gradient-to-r from-teal-100 to-cyan-100 text-teal-800 border-teal-200",
            packed: "bg-gradient-to-r from-pink-100 to-rose-100 text-pink-800 border-pink-200",
            shipped: "bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 border-blue-200",
            delivered: "bg-gradient-to-r from-green-100 to-emerald-100 text-[#008000] border-green-200",
            cancelled: "bg-gradient-to-r from-red-100 to-pink-100 text-red-800 border-red-200",
            returned: "bg-gradient-to-r from-gray-100 to-slate-100 text-gray-800 border-gray-200",
        };
        return colors[status] || "bg-gray-100 text-gray-800";
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-green-200 border-t-green-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-4xl font-bold bg-gradient-to-r from-[#008000] to-emerald-600 bg-clip-text text-transparent">
                        Order Management
                    </h2>
                    <p className="text-gray-600 text-lg font-medium mt-2">Manage customer orders and their status</p>
                </div>
                <button
                    onClick={handleExportToExcel}
                    className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-[#008000] to-emerald-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
                    title="Export Orders to Excel"
                >
                    <ArrowDownTrayIcon className="h-5 w-5" />
                    <span>Export to Excel</span>
                </button>
            </div>

            {/* Search and Filters */}
            <div className="bg-gradient-to-br from-white to-gray-50 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900 flex items-center">
                        <FunnelIcon className="h-5 w-5 mr-2 text-[#008000]" />
                        Search & Filter Orders
                    </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="relative">
                        <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-3.5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search orders by ID, customer name, or email..."
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    if (searchTimeoutRef.current) {
                                        clearTimeout(searchTimeoutRef.current);
                                    }
                                    setSearchTerm(searchInput);
                                    setCurrentPage(1);
                                }
                            }}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-white/70 backdrop-blur-sm"
                        />
                    </div>

                    <select
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)}
                        className="border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-white/70 backdrop-blur-sm"
                    >
                        <option value="">All Status</option>
                        {orderStatuses.length > 0 ? (
                            orderStatuses.map(status => (
                                <option key={status.value || status} value={status.value || status}>
                                    {status.label || status.charAt(0).toUpperCase() + status.slice(1).replace(/-/g, " ")}
                                </option>
                            ))
                        ) : (
                            <>
                                <option value="pending">Pending</option>
                                <option value="confirmed">Confirmed</option>
                                <option value="designing">Designing</option>
                                <option value="proof-sent">Proof Sent</option>
                                <option value="proof-approved">Proof Approved</option>
                                <option value="printing">Printing</option>
                                <option value="quality-check">Quality Check</option>
                                <option value="packed">Packed</option>
                                <option value="shipped">Shipped</option>
                                <option value="delivered">Delivered</option>
                                <option value="cancelled">Cancelled</option>
                                <option value="returned">Returned</option>
                            </>
                        )}
                    </select>

                    <select
                        value={paymentStatusFilter}
                        onChange={e => setPaymentStatusFilter(e.target.value)}
                        className="border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-white/70 backdrop-blur-sm"
                    >
                        <option value="">All Payment Status</option>
                        <option value="paid">Success</option>
                        <option value="pending">Pending</option>
                        <option value="failed">Failed</option>
                        <option value="refunded">Refunded</option>
                    </select>

                    <select
                        value={deliveryTypeFilter}
                        onChange={e => setDeliveryTypeFilter(e.target.value)}
                        className="border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-white/70 backdrop-blur-sm"
                    >
                        <option value="">All Delivery Types</option>
                        <option value="standard">Standard Delivery</option>
                        <option value="express">Express Delivery</option>
                        <option value="self-pickup">Self Pickup</option>
                    </select>
                </div>
            </div>

            {/* Orders Table - Desktop */}
            <div className="hidden lg:block bg-gradient-to-br from-white to-gray-50 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gradient-to-r from-green-500 to-emerald-500 text-white">
                            <tr>
                                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                                    Order ID
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                                    Customer
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                                    Total
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                                    Items
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                                    Payment
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                                    Delivery
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Date</th>
                                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white/80 backdrop-blur-sm divide-y divide-gray-200">
                            {orders.map((order) => (
                                <tr
                                    key={order._id}
                                    className="hover:bg-green-50/50 transition-colors"
                                >
                                    <td className="px-6 py-5 whitespace-nowrap">
                                        <div className="text-sm font-bold text-gray-900">
                                            #{order.orderNumber || order._id.slice(-6)}
                                        </div>
                                    </td>
                                    <td className="px-6 py-5 whitespace-nowrap">
                                        <div className="text-sm font-bold text-gray-900">
                                            {order.user?.name ||
                                                order.shippingAddress?.firstName +
                                                " " +
                                                order.shippingAddress?.lastName ||
                                                "Guest"}
                                        </div>
                                        <div className="text-sm text-gray-600 font-medium">
                                            {order.user?.email || order.shippingAddress?.email || "N/A"}
                                        </div>
                                    </td>
                                    <td className="px-6 py-5 whitespace-nowrap text-sm font-bold text-[#008000]">
                                        ₹{order.pricing?.total || order.totalAmount || 0}
                                    </td>
                                    <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-900 font-medium">
                                        <div className="flex items-center">
                                            <div className="w-2 h-2 bg-blue-400 rounded-full mr-2"></div>
                                            {order.items?.length || 0} item{order.items?.length !== 1 ? "s" : ""}
                                        </div>
                                    </td>
                                    <td className="px-6 py-5 whitespace-nowrap">
                                        <select
                                            value={order.payment?.status || "pending"}
                                            onChange={e => {
                                                const newStatus = e.target.value;
                                                const notes = prompt(`Update payment status to "${newStatus}". Add notes (optional):`);
                                                if (notes !== null) {
                                                    handlePaymentStatusUpdate(order._id, newStatus, notes || "");
                                                }
                                            }}
                                            className={`text-xs font-bold rounded-full px-3 py-1 border-none outline-none transition-colors ${order.payment?.status === "paid"
                                                ? "bg-gradient-to-r from-green-100 to-emerald-100 text-[#008000]"
                                                : order.payment?.status === "failed"
                                                    ? "bg-gradient-to-r from-red-100 to-pink-100 text-red-800"
                                                    : order.payment?.status === "refunded"
                                                        ? "bg-gradient-to-r from-purple-100 to-violet-100 text-purple-800"
                                                        : "bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-800"
                                                }`}
                                        >
                                            <option value="pending">Pending</option>
                                            <option value="paid">Paid</option>
                                            <option value="failed">Failed</option>
                                            <option value="refunded">Refunded</option>
                                        </select>
                                    </td>
                                    <td className="px-6 py-5 whitespace-nowrap">
                                        <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${order.deliveryType === 'express'
                                            ? 'bg-gradient-to-r from-orange-100 to-amber-100 text-orange-800 border border-orange-200'
                                            : order.deliveryType === 'standard'
                                                ? 'bg-gradient-to-r from-blue-100 to-sky-100 text-blue-800 border border-blue-200'
                                                : order.deliveryType === 'self-pickup'
                                                    ? 'bg-gradient-to-r from-gray-100 to-slate-100 text-gray-800 border border-gray-200'
                                                    : 'bg-gradient-to-r from-blue-100 to-sky-100 text-blue-800 border border-blue-200'
                                            }`}>
                                            {order.deliveryType === 'express' ? 'Express' :
                                                order.deliveryType === 'standard' ? 'Standard' :
                                                    order.deliveryType === 'self-pickup' ? 'Self Pickup' :
                                                        order.deliveryType || 'Standard'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-5 whitespace-nowrap">
                                        <select
                                            value={order.status}
                                            onChange={e => handleStatusUpdate(order._id, e.target.value)}
                                            className={`text-xs font-bold rounded-full px-3 py-1 border-none outline-none transition-colors ${getStatusColor(
                                                order.status
                                            )}`}
                                        >
                                            <option value="pending">Pending</option>
                                            <option value="confirmed">Confirmed</option>
                                            <option value="designing">Designing</option>
                                            <option value="proof-sent">Proof Sent</option>
                                            <option value="proof-approved">Proof Approved</option>
                                            <option value="printing">Printing</option>
                                            <option value="quality-check">Quality Check</option>
                                            <option value="packed">Packed</option>
                                            <option value="shipped">Shipped</option>
                                            <option value="delivered">Delivered</option>
                                            <option value="cancelled">Cancelled</option>
                                            <option value="returned">Returned</option>
                                        </select>
                                    </td>
                                    <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-600 font-medium">
                                        {new Date(order.createdAt).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-5 whitespace-nowrap text-sm font-medium">
                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={() => handleViewOrder(order._id)}
                                                className="p-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg hover:shadow-lg transition-shadow"
                                                title="View Order Details"
                                            >
                                                <EyeIcon className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteOrder(order._id, order.orderNumber)}
                                                className="p-2 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-lg hover:shadow-lg transition-shadow"
                                                title="Delete Order"
                                            >
                                                <TrashIcon className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="bg-gradient-to-r from-gray-50 to-white px-6 py-4 flex items-center justify-between border-t border-gray-200">
                        <div className="flex-1 flex justify-between sm:hidden">
                            <button
                                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                disabled={currentPage === 1}
                                className="relative inline-flex items-center px-6 py-3 border border-gray-300 text-sm font-bold rounded-xl text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                disabled={currentPage === totalPages}
                                className="ml-3 relative inline-flex items-center px-6 py-3 border border-gray-300 text-sm font-bold rounded-xl text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
                            >
                                Next
                            </button>
                        </div>
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                                <p className="text-sm text-gray-700 font-medium">
                                    Page <span className="font-bold text-[#008000]">{currentPage}</span> of{" "}
                                    <span className="font-bold text-[#008000]">{totalPages}</span>
                                </p>
                            </div>
                            <div className="flex space-x-2">
                                {[...Array(Math.min(ADMIN_CONFIG.pagination.maxVisiblePages, totalPages))].map((_, i) => {
                                    const pageNum = i + 1;
                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => setCurrentPage(pageNum)}
                                            className={`px-4 py-2 text-sm font-bold rounded-lg transition-colors ${pageNum === currentPage
                                                ? "bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg"
                                                : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                                                }`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Orders Cards - Mobile */}
            <div className="lg:hidden space-y-4">
                {orders.map((order) => (
                    <div
                        key={order._id}
                        className="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-lg p-4 hover:shadow-xl transition-shadow"
                    >
                        {/* Card Header */}
                        <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                    <span className="text-lg font-bold text-gray-900">
                                        #{order.orderNumber || order._id.slice(-6)}
                                    </span>
                                    <span
                                        className={`inline-flex px-2 py-1 text-xs font-bold rounded-full ${order.payment?.status === "paid"
                                            ? "bg-green-100 text-[#008000]"
                                            : order.payment?.status === "failed"
                                                ? "bg-red-100 text-red-800"
                                                : "bg-yellow-100 text-yellow-800"
                                            }`}
                                    >
                                        {order.payment?.status || "pending"}
                                    </span>
                                </div>
                                <div className="text-sm text-gray-600">
                                    {new Date(order.createdAt).toLocaleDateString("en-US", {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                    })}
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-lg font-bold text-[#008000]">
                                    ₹{order.pricing?.total || order.totalAmount || 0}
                                </div>
                                <div className="text-sm text-gray-500">
                                    {order.items?.length || 0} item{order.items?.length !== 1 ? "s" : ""}
                                </div>
                            </div>
                        </div>

                        {/* Customer Info */}
                        <div className="bg-gray-50 rounded-xl p-3 mb-4">
                            <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                                    <span className="text-white text-sm font-bold">
                                        {(order.user?.name || order.shippingAddress?.firstName || "G")
                                            .charAt(0)
                                            .toUpperCase()}
                                    </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-gray-900 truncate">
                                        {order.user?.name ||
                                            order.shippingAddress?.firstName +
                                            " " +
                                            order.shippingAddress?.lastName ||
                                            "Guest"}
                                    </div>
                                    <div className="text-xs text-gray-500 truncate">
                                        {order.user?.email || order.shippingAddress?.email || "No email"}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Delivery Type */}
                        <div className="mb-4">
                            <div className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
                                <span className="text-xs font-medium text-gray-700">Delivery Type:</span>
                                <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${order.deliveryType === 'express'
                                    ? 'bg-gradient-to-r from-orange-100 to-amber-100 text-orange-800 border border-orange-200'
                                    : order.deliveryType === 'standard'
                                        ? 'bg-gradient-to-r from-blue-100 to-sky-100 text-blue-800 border border-blue-200'
                                        : order.deliveryType === 'self-pickup'
                                            ? 'bg-gradient-to-r from-gray-100 to-slate-100 text-gray-800 border border-gray-200'
                                            : 'bg-gradient-to-r from-blue-100 to-sky-100 text-blue-800 border border-blue-200'
                                    }`}>
                                    {order.deliveryType === 'express' ? 'Express' :
                                        order.deliveryType === 'standard' ? 'Standard' :
                                            order.deliveryType === 'self-pickup' ? 'Self Pickup' :
                                                order.deliveryType || 'Standard'}
                                </span>
                            </div>
                        </div>

                        {/* Status and Actions */}
                        <div className="space-y-3 mb-4">
                            <div className="flex-1">
                                <label className="block text-xs font-medium text-gray-700 mb-1">Order Status</label>
                                <select
                                    value={order.status}
                                    onChange={e => handleStatusUpdate(order._id, e.target.value)}
                                    className={`w-full text-sm font-medium rounded-xl px-3 py-2 border-0 outline-none transition-colors ${getStatusColor(
                                        order.status
                                    )}`}
                                >
                                    {orderStatuses.length > 0 ? (
                                        orderStatuses.map(status => (
                                            <option key={status.value || status} value={status.value || status}>
                                                {status.label || status.charAt(0).toUpperCase() + status.slice(1).replace(/-/g, " ")}
                                            </option>
                                        ))
                                    ) : (
                                        <>
                                            <option value="pending">Pending</option>
                                            <option value="confirmed">Confirmed</option>
                                            <option value="designing">Designing</option>
                                            <option value="proof-sent">Proof Sent</option>
                                            <option value="proof-approved">Proof Approved</option>
                                            <option value="printing">Printing</option>
                                            <option value="quality-check">Quality Check</option>
                                            <option value="packed">Packed</option>
                                            <option value="shipped">Shipped</option>
                                            <option value="delivered">Delivered</option>
                                            <option value="cancelled">Cancelled</option>
                                            <option value="returned">Returned</option>
                                        </>
                                    )}
                                </select>
                            </div>
                            <div className="flex-1">
                                <label className="block text-xs font-medium text-gray-700 mb-1">Payment Status</label>
                                <select
                                    value={order.payment?.status || "pending"}
                                    onChange={e => {
                                        const newStatus = e.target.value;
                                        const notes = prompt(`Update payment status to "${newStatus}". Add notes (optional):`);
                                        if (notes !== null) {
                                            handlePaymentStatusUpdate(order._id, newStatus, notes || "");
                                        }
                                    }}
                                    className={`w-full text-sm font-medium rounded-xl px-3 py-2 border-0 outline-none transition-colors ${order.payment?.status === "paid"
                                        ? "bg-gradient-to-r from-green-100 to-emerald-100 text-[#008000]"
                                        : order.payment?.status === "failed"
                                            ? "bg-gradient-to-r from-red-100 to-pink-100 text-red-800"
                                            : order.payment?.status === "refunded"
                                                ? "bg-gradient-to-r from-purple-100 to-violet-100 text-purple-800"
                                                : "bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-800"
                                        }`}
                                >
                                    <option value="pending">Pending</option>
                                    <option value="paid">Paid</option>
                                    <option value="failed">Failed</option>
                                    <option value="refunded">Refunded</option>
                                </select>
                            </div>
                        </div>
                        <div className="flex space-x-2">
                            <button
                                onClick={() => handleViewOrder(order._id)}
                                className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-4 py-2 rounded-xl font-medium text-sm shadow-lg hover:shadow-xl transition-shadow flex items-center justify-center space-x-2"
                            >
                                <EyeIcon className="h-4 w-4" />
                                <span>View</span>
                            </button>
                            <button
                                onClick={() => handleDeleteOrder(order._id, order.orderNumber)}
                                className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-4 py-2 rounded-xl font-medium text-sm shadow-lg hover:shadow-xl transition-shadow flex items-center justify-center"
                                title="Delete Order"
                            >
                                <TrashIcon className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Order Details Dialog */}
            <OrderDetailsDialog
                isOpen={showOrderDialog}
                onClose={handleCloseDialog}
                orderId={selectedOrderId}
                onOrderDeleted={() => {
                    fetchOrders();
                    refreshOrders();
                }}
            />
        </div>
    );
};

export default OrderManagement;
